import { useCallback, useEffect, useRef } from 'react';
import { useAnimeFxContext } from './AnimeFxProvider';
import { FLY_POOL_SIZE, CART_BUMP_DURATION_MS } from './fxConstants';
import { playDing } from './sfx';

type SpawnFlyFn = (sourceEl: HTMLElement, targetEl: HTMLElement, imageSrc: string) => void;

/**
 * Renders the portal container and registers the spawn function on the
 * Provider's spawnFlyRef. Maintains a fixed-size <img> object pool.
 */
export default function FlyToCartLayer() {
  const ctx = useAnimeFxContext();
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Pre-allocate img pool.
    const pool: HTMLImageElement[] = [];
    for (let i = 0; i < FLY_POOL_SIZE; i += 1) {
      const img = document.createElement('img');
      img.className = 'cart-fly';
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      root.appendChild(img);
      pool.push(img);
    }
    const free = new Set(pool);

    const acquire = (): HTMLImageElement | null => {
      const it = free.values().next();
      if (it.done) return null;
      free.delete(it.value);
      return it.value;
    };

    const release = (img: HTMLImageElement) => {
      img.classList.remove('fly-active');
      img.removeAttribute('src');
      free.add(img);
    };

    // Arrival cue: the cart bump + ding that confirm a line was added.
    // Must run on every quick-add — even when there is no image to fly —
    // so the button never feels dead.
    const confirmArrival = (targetEl: HTMLElement) => {
      targetEl.classList.add('cart-bump');
      playDing();
      window.setTimeout(() => targetEl.classList.remove('cart-bump'), CART_BUMP_DURATION_MS);
    };

    const spawn: SpawnFlyFn = (sourceEl, targetEl, imageSrc) => {
      // Image-less product: nothing to fly, but the add still happened.
      // Confirm it directly instead of feeding an empty src to an <img>
      // (an empty src fires `error`, which would otherwise cancel the cue).
      if (!imageSrc) { confirmArrival(targetEl); return; }

      const s = sourceEl.getBoundingClientRect();
      const t = targetEl.getBoundingClientRect();
      const startX = s.left + s.width / 2;
      const startY = s.top + s.height / 2;
      const dx = (t.left + t.width / 2) - startX;
      const dy = (t.top + t.height / 2) - startY;
      const peakY = Math.min(dy, -120);

      const img = acquire();
      if (!img) { confirmArrival(targetEl); return; } // pool exhausted — skip fly, keep cue

      img.style.left = `${startX}px`;
      img.style.top = `${startY}px`;
      img.style.setProperty('--dx', `${dx}px`);
      img.style.setProperty('--dy', `${dy}px`);
      img.style.setProperty('--peak', `${peakY}px`);

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        img.removeEventListener('animationend', finish);
        img.onerror = null;
        release(img);
        confirmArrival(targetEl);
      };
      // A broken image URL must not swallow the arrival cue either.
      img.onerror = finish;
      img.addEventListener('animationend', finish);

      img.src = imageSrc;
      void img.offsetWidth; // restart animation cleanly
      img.classList.add('fly-active');
    };

    ctx.spawnFlyRef.current = spawn;
    return () => {
      ctx.spawnFlyRef.current = null;
      pool.forEach((img) => img.remove());
    };
    // ctx is stable; spawnFlyRef identity never changes. Linter may want it; safe either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={rootRef} className="fx-fly-root" aria-hidden="true" />;
}

/**
 * Returns a fire-and-forget callback. Safe to call when no target is registered,
 * fxEnabled is false, or the layer hasn't mounted yet.
 */
export function useFlyToCart() {
  const ctx = useAnimeFxContext();
  return useCallback((sourceEl: HTMLElement, imageSrc: string) => {
    if (!ctx.fxEnabled) return;
    const target = ctx.cartTargetRef.current;
    const spawn = ctx.spawnFlyRef.current;
    if (!target || !spawn) return;
    try { spawn(sourceEl, target, imageSrc); } catch { /* never let fx crash business */ }
  }, [ctx]);
}

// Every mounted cart button, oldest first. The newest one is the target; when
// it unmounts the previous one takes over again — the mobile floating cart
// (shown while the header is scrolled away) lends itself as the target and
// hands it back to the header's cart when it disappears.
const cartTargets = new WeakMap<object, HTMLElement[]>();

/** Returns a ref-callback the cart button passes to its DOM node. */
export function useRegisterCartTarget() {
  const ctx = useAnimeFxContext();
  const own = useRef<HTMLElement | null>(null);
  return useCallback((el: HTMLElement | null) => {
    const stack = cartTargets.get(ctx.cartTargetRef) ?? [];
    const i = own.current ? stack.lastIndexOf(own.current) : -1;
    if (i >= 0) stack.splice(i, 1);
    own.current = el;
    if (el) stack.push(el);
    cartTargets.set(ctx.cartTargetRef, stack);
    ctx.cartTargetRef.current = stack[stack.length - 1] ?? null;
  }, [ctx]);
}
